'use client';

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { createClient } from '@/lib/supabase/client';
import { notifyAuthIdle } from '@/lib/supabase/sessionReady';

interface AdminUserProfile {
  id: string;
  username: string;
  fullName: string;
  email: string;
  role: 'admin' | 'manager' | 'assistant';
  status: 'active' | 'inactive';
  authUserId: string;
}

interface AuthContextType {
  user: any;
  session: any;
  adminUser: AdminUserProfile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<any>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const supabase = createClient();

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [adminUser, setAdminUser] = useState<AdminUserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingAdminUserId, setPendingAdminUserId] = useState<string | null>(null);

  const initializedRef = useRef(false);
  const lastSessionRef = useRef<any>(null);

  const fetchAdminUser = async (authUserId: string): Promise<AdminUserProfile | null> => {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('id, username, full_name, email, role, status, auth_user_id')
        .eq('auth_user_id', authUserId)
        .eq('status', 'active')
        .maybeSingle();
      if (error || !data) return null;
      return {
        id: data.id,
        username: data.username,
        fullName: data.full_name,
        email: data.email,
        role: data.role as 'admin' | 'manager' | 'assistant',
        status: data.status as 'active' | 'inactive',
        authUserId: data.auth_user_id,
      };
    } catch {
      return null;
    }
  };

  // Load admin profile outside onAuthStateChange to avoid Supabase auth lock deadlocks
  useEffect(() => {
    if (!pendingAdminUserId) {
      if (!user) setAdminUser(null);
      return;
    }
    let cancelled = false;
    fetchAdminUser(pendingAdminUserId).then((admin) => {
      if (!cancelled) setAdminUser(admin);
    });
    return () => {
      cancelled = true;
    };
  }, [pendingAdminUserId, user]);

  useEffect(() => {
    let mounted = true;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, newSession: Session | null) => {
      if (!mounted) return;

      if (event === 'TOKEN_REFRESHED') {
        if (newSession) {
          lastSessionRef.current = newSession;
          setSession(newSession);
          setUser(newSession.user);
        }
        notifyAuthIdle();
        if (!initializedRef.current) {
          initializedRef.current = true;
          setLoading(false);
        }
        return;
      }

      if (event === 'SIGNED_OUT') {
        lastSessionRef.current = null;
        setSession(null);
        setUser(null);
        setAdminUser(null);
        setPendingAdminUserId(null);
        notifyAuthIdle();
        if (!initializedRef.current) {
          initializedRef.current = true;
          setLoading(false);
        }
        return;
      }

      if (newSession?.user) {
        lastSessionRef.current = newSession;
        setSession(newSession);
        setUser(newSession.user);
        setPendingAdminUserId(newSession.user.id);
      } else if (!lastSessionRef.current) {
        setSession(null);
        setUser(null);
        setAdminUser(null);
        setPendingAdminUserId(null);
      }

      if (!initializedRef.current) {
        initializedRef.current = true;
        setLoading(false);
      }
    });

    supabase.auth.getSession().then(({ data: { session: existingSession } }: { data: { session: Session | null } }) => {
      if (!mounted || initializedRef.current) return;
      lastSessionRef.current = existingSession;
      setSession(existingSession);
      setUser(existingSession?.user ?? null);
      if (existingSession?.user) {
        setPendingAdminUserId(existingSession.user.id);
      }
      initializedRef.current = true;
      setLoading(false);
      notifyAuthIdle();
    }).catch(() => {
      if (mounted && !initializedRef.current) {
        initializedRef.current = true;
        setLoading(false);
        notifyAuthIdle();
      }
    });

    const safetyTimer = setTimeout(() => {
      if (mounted && !initializedRef.current) {
        initializedRef.current = true;
        setLoading(false);
        notifyAuthIdle();
      }
    }, 4000);

    return () => {
      mounted = false;
      subscription.unsubscribe();
      clearTimeout(safetyTimer);
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    lastSessionRef.current = null;
    setAdminUser(null);
    setPendingAdminUserId(null);
  };

  const getCurrentUser = async () => {
    const { data: { user: currentUser }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return currentUser;
  };

  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value: AuthContextType = {
    user,
    session,
    adminUser,
    loading,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
